import { ICorporationResponse } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import { CorporationsDataSource } from 'custom_hooks/data_hooks/data_source/corporationDataSource';
import { CorporationAbbreviationCell } from 'pages/Settings/Corporations/table_cells/CorporationAbbreviationCell';
import { CorporationActiveCell } from 'pages/Settings/Corporations/table_cells/CorporationActiveCell';
import { CorporationIndustryCell } from 'pages/Settings/Corporations/table_cells/CorporationIndustryCell';
import { CorporationNameCell } from 'pages/Settings/Corporations/table_cells/CorporationNameCell';
import { EditCorporationTimeRangeCell } from 'pages/Settings/Corporations/table_cells/EditCorporationTimeRangeCell';
import { useCallback, useMemo } from 'react';
import { getId } from 'utils/data';

export type CorporationsTableProps = Readonly<{
    dataSource: CorporationsDataSource;
}>;

export type CorporationsTableCellProps = Readonly<{
    rowData: ICorporationResponse;
    onChange: (updated: ICorporationResponse) => void;
    className?: string;
}>;

export function CorporationsTable({ dataSource }: CorporationsTableProps) {
    const { canEditCorporationSettings } = usePermissions();
    const user = useUser();
    const { updateData, reloadPage } = dataSource;
    const updateRow = useCallback(
        (newRowData: ICorporationResponse) => updateData([newRowData], getId),
        [updateData],
    );

    const handleActiveChange = useCallback(
        (newRowData: ICorporationResponse) => {
            updateRow(newRowData);
            user.updateFavorites();
        },
        [updateRow, user],
    );

    const columns = useMemo<TableColumn<ICorporationResponse>[]>(
        () => [
            {
                header: () => 'Corporation Name',
                id: 'Name',
                value: ({ rowData }) => (
                    <CorporationNameCell rowData={rowData} onChange={reloadPage} />
                ),
                sortOn: 'Name',
                primary: true,
                locked: true,
            },
            {
                header: () => 'Abbreviation',
                id: 'Abbreviation',
                value: ({ rowData }) => (
                    <CorporationAbbreviationCell rowData={rowData} onChange={updateRow} />
                ),
                sortOn: 'Abbreviation',
            },
            {
                header: () => 'Industry',
                id: 'IndustryId',
                value: ({ rowData }) => (
                    <CorporationIndustryCell rowData={rowData} onChange={updateRow} />
                ),
            },
            {
                header: () => 'Active',
                id: 'Active',
                value: ({ rowData }) => (
                    <CorporationActiveCell
                        rowData={rowData}
                        onChange={handleActiveChange}
                    />
                ),
                sortOn: 'Active',
            },
            {
                id: 'actions',
                header: () => 'Actions',
                value: ({ rowData }) => (
                    <ActionMenuTableCell>
                        {canEditCorporationSettings && (
                            <EditCorporationTimeRangeCell
                                rowData={rowData}
                                onChange={updateRow}
                            />
                        )}
                    </ActionMenuTableCell>
                ),
                locked: true,
            },
        ],
        [updateRow, reloadPage, canEditCorporationSettings, handleActiveChange],
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getId}
            columns={columns}
            noResultMessage="No corporations found."
        />
    );
}
